import { Plus } from 'lucide-react';
import { Button, Menu, MenuButton, MenuItem, MenuList, Stack, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import type { Editor } from '@tiptap/react';
import type { TemplateVariable } from '@duke-hq/email-templates';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

/** Inserting a value rather than typing one, so a path cannot be misspelled. */
export function VariablePicker({
  editor,
  variables,
}: {
  editor: Editor | null;
  variables: TemplateVariable[];
}): React.ReactElement {
  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        leftIcon={<Plus size={iconSize.sm} />}
        isDisabled={editor === null}
        data-testid={testIds.variablePicker}
      >
        <Trans>Insert a value</Trans>
      </MenuButton>
      <MenuList maxHeight={pxToRem(320)} overflowY="auto">
        {variables.map((variable) => (
          <MenuItem
            key={variable.path}
            onClick={() =>
              editor
                ?.chain()
                .focus()
                .insertContent({ type: 'variable', attrs: { path: variable.path } })
                .run()
            }
          >
            <Stack gap={0}>
              <Text fontSize="sm">{variable.description ?? variable.path}</Text>
              <Text fontSize="xs" color="fg.muted">
                {variable.path}
                {variable.optional ? <Trans> — sometimes missing</Trans> : null}
              </Text>
            </Stack>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
